/**
 * RisikoprofilTab - Ausschreibungs-Screen Tab 1 (P2 Placement Slice 5).
 *
 * Renders the frozen Kunde general snapshot (READ-ONLY, with «Im Kundenprofil bearbeiten» +
 * «Snapshot aktualisieren») and the EDITABLE branch Ist snapshot (one card per insured object,
 * reusing the Slice-2 IstAttributeSection via a toPoliceRisikoObjekt adapter + onPatch →
 * patchBranchIst), plus the read-only Schadenverlauf reference to the Referenzpolice (Slice 6, Ch 7.3).
 *
 * [Source: apps/web/src/features/entity-detail/components/police-risiko/IstAttributeSection.tsx]
 */

import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { RefreshCw } from 'lucide-react';
import {
  GESUNDHEITSPRUEFUNG_STATUS_LABELS,
  EINKOMMENS_BANDBREITE_LABELS,
  RISIKOPROFIL_KATEGORIE_LABELS,
  risikoprofilOptionLabel,
} from '@brokly/shared';
import type {
  BranchenFeldDefinition,
  PoliceRisikoObjekt,
  PoliceRisikoPatchBody,
  PlacementSnapshotObjektWire,
  SnapshotKundeFeld,
} from '@brokly/shared';
import { Button } from '../../components/ui/Button';
import { EntityLink } from '../../components/ui/EntityLink';
import { chf, dateShortDe } from '../../lib/format';
import { PlacementSchadenverlaufRef } from './PlacementSchadenverlaufRef';
import { RisikoprofilFeldInput } from '../entity-detail/components/risikoprofil/RisikoprofilFeldInput';
import { StatusDot } from '../../components/ui/StatusDot';
import { AMPEL_LABEL, AMPEL_TONE } from '../entity-detail/components/risikoprofil/ampel';
import { IstAttributeSection } from '../entity-detail/components/police-risiko/IstAttributeSection';
import { PlacementHinweisbox } from './PlacementHinweisbox';
import { usePlacementRisikoprofil, usePatchBranchIst, useRefreshGeneral } from './usePlacementSnapshot';

/** Adapt a snapshot branch object to the PoliceRisikoObjekt the IstAttributeSection consumes. */
function toPoliceRisikoObjekt(o: PlacementSnapshotObjektWire): PoliceRisikoObjekt {
  return {
    id: o.insurance_object_ref ?? 'policy',
    insurance_object_ref: o.insurance_object_ref,
    object_label: o.object_label,
    attributes: o.attributes,
    source: o.origin_source ?? 'manuell',
    filled: o.filled,
    total: o.total,
    updated_at: o.updated_at,
  };
}

export function RisikoprofilTab({
  placementId,
  subjectId,
  subjectType,
  canWrite,
}: {
  placementId: string;
  subjectId: string;
  subjectType: 'person' | 'firma';
  canWrite: boolean;
}) {
  const navigate = useNavigate();
  const query = usePlacementRisikoprofil(placementId);
  const patch = usePatchBranchIst(placementId);
  const refresh = useRefreshGeneral(placementId);
  const [newRef, setNewRef] = useState('');

  if (query.isLoading) return <p className="p-4 text-sm text-muted-foreground">Lade Risikoprofil…</p>;
  if (query.isError || !query.data) {
    return <p className="p-4 text-sm text-status-danger" data-testid="plt1-error">Risikoprofil konnte nicht geladen werden.</p>;
  }

  const data = query.data;
  const editable = data.editable && canWrite;
  const branchFelder: BranchenFeldDefinition[] = data.branch
    ? data.branch.felder.map((f) => ({ ...f, branchencode: data.branch!.branchencode }))
    : [];

  const kundeFelder = (data.general?.felder ?? []) as SnapshotKundeFeld[];
  const kategorien = Array.from(new Set(kundeFelder.map((f) => f.kategorie)));

  function doPatch(body: PoliceRisikoPatchBody) {
    patch.mutate({
      insurance_object_ref: body.insurance_object_ref,
      object_label: body.object_label,
      attributes: body.attributes ?? {},
    });
  }

  return (
    <div className="flex flex-col gap-4 py-4" data-testid="placement-risikoprofil-tab">
      <PlacementHinweisbox />

      {(patch.isError || refresh.isError) && (
        <div className="rounded-lg border border-status-danger/40 bg-status-danger/5 p-3 text-sm text-status-danger" role="alert" data-testid="plt1-mutation-error">
          Speichern fehlgeschlagen. Bitte Eingaben pruefen und erneut versuchen.
        </div>
      )}

      {/* ── General (frozen Kunde Risikoprofil, read-only) ── */}
      <section className="rounded-lg border bg-card p-4" data-testid="plt1-general">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-foreground">Generelles Risikoprofil (Snapshot)</h3>
          {data.general && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground" data-testid="plt1-general-count">
                {data.general.filled}/{data.general.total} generell gespeichert
              </span>
              <EntityLink
                onActivate={() =>
                  void navigate({ to: '/kunden/$id', params: { id: subjectId }, search: { tab: 'risikoprofil' } })
                }
              >
                Im Kundenprofil bearbeiten
              </EntityLink>
              {editable && (
                <Button size="sm" variant="outline" onClick={() => refresh.mutate()} disabled={refresh.isPending} data-testid="plt1-refresh">
                  <RefreshCw className="mr-1 h-3.5 w-3.5" aria-hidden />
                  {refresh.isPending ? 'Aktualisiere…' : 'Snapshot aktualisieren'}
                </Button>
              )}
            </div>
          )}
        </div>

        {!data.general ? (
          <p className="mt-3 text-sm text-muted-foreground" data-testid="plt1-general-hidden">
            Kein Zugriff auf das generelle Risikoprofil (fehlende Berechtigung).
          </p>
        ) : (
          <div className="mt-3 flex flex-col gap-4">
            {kategorien.map((kat) => (
              <div key={kat}>
                <h4 className="text-xs font-semibold uppercase text-muted-foreground">{RISIKOPROFIL_KATEGORIE_LABELS[kat]}</h4>
                <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {kundeFelder.filter((f) => f.kategorie === kat).map((f) => (
                    <RisikoprofilFeldInput key={f.key} def={f} value={data.general!.attributes[f.key] ?? null} editing={false} optionLabel={risikoprofilOptionLabel} onChange={() => {}} />
                  ))}
                </div>
              </div>
            ))}

            {subjectType === 'person' && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="plt1-gated">
                <GatedRow
                  label="Gesundheitspruefung"
                  present={data.general.gesundheitspruefung_status !== undefined}
                  value={data.general.gesundheitspruefung_status ? GESUNDHEITSPRUEFUNG_STATUS_LABELS[data.general.gesundheitspruefung_status] : '-'}
                />
                <GatedRow
                  label="Einkommens-Bandbreite"
                  present={data.general.einkommens_bandbreite !== undefined}
                  value={data.general.einkommens_bandbreite ? EINKOMMENS_BANDBREITE_LABELS[data.general.einkommens_bandbreite] : '-'}
                />
              </div>
            )}

            {data.general.basissparten.length > 0 && (
              <div data-testid="plt1-basissparten">
                <h4 className="text-xs font-semibold uppercase text-muted-foreground">Basissparten</h4>
                <ul className="mt-2 flex flex-col gap-1">
                  {data.general.basissparten.map((b) => (
                    <li key={b.sparte_key} className="flex items-center gap-2 text-sm">
                      <StatusDot tone={AMPEL_TONE[b.ampel]} label={AMPEL_LABEL[b.ampel]} />
                      <span>{b.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Schadenverlauf je Sparte (P2 hardening Q15) - frozen, read-only (Tab 1 is a snapshot). */}
            <div data-testid="plt1-schaeden-sparte">
              <h4 className="text-xs font-semibold uppercase text-muted-foreground">Schadenverlauf je Sparte</h4>
              {data.general.schaeden_je_sparte === undefined ? (
                <p className="mt-2 text-sm text-muted-foreground">
                  Schaden je Sparte: nicht im Snapshot (vor Aktualisierung erstellt)
                </p>
              ) : data.general.schaeden_je_sparte.length === 0 ? (
                <p className="mt-2 text-sm text-muted-foreground">Keine Schaeden je Sparte erfasst</p>
              ) : (
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full min-w-[560px] text-left text-sm">
                    <thead>
                      <tr className="border-b text-xs text-muted-foreground">
                        <th className="py-1 pr-3 font-medium">Sparte</th>
                        <th className="py-1 pr-3 font-medium">Anzahl</th>
                        <th className="py-1 pr-3 font-medium">Summe CHF</th>
                        <th className="py-1 pr-3 font-medium">Art</th>
                        <th className="py-1 pr-3 font-medium">Zeitraum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.general.schaeden_je_sparte.map((s) => (
                        <tr key={s.branchencode} className="border-b last:border-0 align-top">
                          <td className="py-1 pr-3 text-foreground">{s.label}</td>
                          <td className="py-1 pr-3 text-foreground">{s.anzahl_schaeden}</td>
                          <td className="py-1 pr-3 text-foreground">{s.schadensumme === null ? '-' : chf(s.schadensumme)}</td>
                          <td className="py-1 pr-3 text-foreground">{s.art_schaeden ?? '-'}</td>
                          <td className="py-1 pr-3 text-foreground">{dateShortDe(s.zeitraum_von)} - {dateShortDe(s.zeitraum_bis)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      {/* ── Branch Ist (editable for this Ausschreibung) ── */}
      <section className="flex flex-col gap-3" data-testid="plt1-branch">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-foreground">Branchenspezifische Angaben (Ist)</h3>
        </div>
        {!data.branch ? (
          <p className="text-sm text-muted-foreground" data-testid="plt1-branch-hidden">
            Kein Zugriff auf die branchenspezifischen Angaben der Police (fehlende Berechtigung).
          </p>
        ) : (
          data.branch.objekte.map((o) => (
            <div key={o.insurance_object_ref ?? 'policy'} className="flex flex-col gap-1">
              <IstAttributeSection
                felder={branchFelder}
                objekt={toPoliceRisikoObjekt(o)}
                canWrite={editable}
                isSaving={patch.isPending}
                onPatch={doPatch}
                gatedSuppressed={[]}
              />
              {o.changed_keys.length > 0 && (
                <span className="text-xs text-status-warning" data-testid="plt1-abweichend">
                  Abweichend zur Police: {o.changed_keys.join(', ')}
                </span>
              )}
            </div>
          ))
        )}

        {editable && !data.scoped_object_ref && (
          <div className="flex items-center gap-2" data-testid="plt1-add-object">
            <input
              value={newRef}
              onChange={(e) => setNewRef(e.target.value)}
              placeholder="Objekt-Referenz"
              aria-label="Neue Objekt-Referenz"
              className="rounded-md border bg-card px-2 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <Button
              size="sm"
              variant="outline"
              disabled={newRef.trim().length === 0 || patch.isPending}
              onClick={() => { doPatch({ insurance_object_ref: newRef.trim(), attributes: {} }); setNewRef(''); }}
            >
              Objekt hinzufuegen
            </Button>
          </div>
        )}
      </section>

      {/* ── Schadenverlauf (read-only ref to the Referenzpolice; Slice 6, Ch 7.3) ── */}
      <PlacementSchadenverlaufRef ref={data.schadenverlauf} policyId={data.policy_id} />
    </div>
  );
}

function GatedRow({ label, present, value }: { label: string; present: boolean; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <span className="text-sm text-foreground">
        {present ? value : 'Nicht im Snapshot (Einwilligung/Berechtigung fehlt)'}
      </span>
    </div>
  );
}
